import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../componentes/modal/modal";
import Button from "../../../../componentes/button/button";
import Input from "../../../../componentes/input/input";
import FleetcomService from "../../../../services/resource/fleetcomService";

const fleetcomService = new FleetcomService();

const filtrosIniciais = { placa: "", codigo: "", modelo: "", tipo: "", centroCusto: "", filial: "" };

function contem(valor, termo) {
    if (!termo.trim()) return true;
    return (valor ?? "").toString().toLowerCase().includes(termo.trim().toLowerCase());
}

/**
 * Busca de veículo via Fleetcom (GET /api/fleetcom/veiculos) — a API não aceita filtros, então ela
 * retorna a frota inteira e os filtros são aplicados no cliente. A Solicitação de Compras não tem
 * relacionamento com a entidade Veiculo — só a placa é armazenada, então a seleção aqui é direta,
 * sem resolver nenhum cadastro interno.
 */
export default function SelecionarVeiculoModal({ isOpen, onClose, onSelecionar }) {
    const [filtros, setFiltros] = useState(filtrosIniciais);
    const [veiculos, setVeiculos] = useState([]);
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        if (!isOpen) return undefined;

        setFiltros(filtrosIniciais);
        setVeiculos([]);
        setCarregando(true);

        let cancelado = false;
        fleetcomService.listarVeiculos()
            .then(res => { if (!cancelado) setVeiculos(res.data); })
            .catch(() => {
                if (!cancelado) {
                    setVeiculos([]);
                    toast.error("Erro ao buscar veículos na Fleetcom.");
                }
            })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [isOpen]);

    function set(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    const temFiltro = Object.values(filtros).some(v => v.trim().length > 0);

    const resultados = useMemo(() => veiculos.filter(v =>
        contem(v.placa, filtros.placa) &&
        contem(v.codigo, filtros.codigo) &&
        contem(v.modelo, filtros.modelo) &&
        contem(v.tipo, filtros.tipo) &&
        contem(v.centroCusto, filtros.centroCusto) &&
        contem(v.filial, filtros.filial)
    ), [veiculos, filtros]);

    function selecionar(veiculo) {
        onSelecionar({
            placa: veiculo.placa,
            modelo: veiculo.modelo,
            codigo: veiculo.codigo,
        });
        onClose();
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Selecionar Veículo"
            subtitle="Busque por Placa, Código, Modelo, Fabricante, Tipo, Centro de Custo ou Filial e selecione o veículo desta linha."
            className="w-[90%] min-h-[70vh]"
            footer={
                <div className="flex justify-end">
                    <Button variant="outline" label="Fechar" onClick={onClose} />
                </div>
            }
        >
            <div className="p-3 py-4 flex flex-col gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Input type="text" label="Placa" value={filtros.placa} onChange={e => set("placa", e.target.value)} />
                    <Input type="text" label="Código" value={filtros.codigo} onChange={e => set("codigo", e.target.value)} />
                    <Input type="text" label="Modelo" value={filtros.modelo} onChange={e => set("modelo", e.target.value)} />
                    <Input type="text" label="Tipo" value={filtros.tipo} onChange={e => set("tipo", e.target.value)} />
                    <Input type="text" label="Centro de Custo" value={filtros.centroCusto} onChange={e => set("centroCusto", e.target.value)} />
                    <Input type="text" label="Filial" value={filtros.filial} onChange={e => set("filial", e.target.value)} />
                </div>

                {carregando && (
                    <div className="text-sm text-muted-foreground text-center py-6">Buscando veículos na Fleetcom...</div>
                )}

                {!carregando && resultados.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">
                        {temFiltro ? "Nenhum veículo encontrado para os filtros informados." : "Nenhum veículo retornado pela Fleetcom."}
                    </div>
                )}

                {!carregando && resultados.length > 0 && (
                    <div className="overflow-x-auto border border-border rounded-lg max-h-[50vh] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-muted sticky top-0">
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 font-semibold">Placa</th>
                                    <th className="px-3 py-2 font-semibold">Código</th>
                                    <th className="px-3 py-2 font-semibold">Modelo</th>
                                    <th className="px-3 py-2 font-semibold">Fabricante</th>
                                    <th className="px-3 py-2 font-semibold">Tipo</th>
                                    <th className="px-3 py-2 font-semibold">Centro de Custo</th>
                                    <th className="px-3 py-2 font-semibold">Filial</th>
                                    <th className="px-3 py-2 font-semibold">Ano Fab./Mod.</th>
                                    <th className="px-3 py-2 font-semibold text-right">KM Rodado</th>
                                    <th className="px-3 py-2 font-semibold">UF</th>
                                    <th className="px-3 py-2 w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {resultados.map(veiculo => (
                                    <tr
                                        key={`${veiculo.codigo}-${veiculo.placa}`}
                                        onDoubleClick={() => selecionar(veiculo)}
                                        className="border-t border-border hover:bg-primary/5 cursor-pointer"
                                    >
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.placa}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.codigo}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.modelo}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.fabricante}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.tipo}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.centroCusto}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.filial}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.anoFabricacao}/{veiculo.anoModelo}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap text-right">
                                            {veiculo.kmRodado != null ? Number(veiculo.kmRodado).toLocaleString("pt-BR") : ""}
                                        </td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{veiculo.estado}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="blue" label="Selecionar" onClick={() => selecionar(veiculo)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
}
